// src/utils/fnctColonnes.ts — SCE Issues
// Colonnes des listes SCE_FNCT et SCE_FNCT_Actions, décrites UNE fois :
// écriture, relecture, comparaison de l'import et vérification que les listes
// ont bien été créées (colonnes manquantes signalées avant toute écriture).
// Pur, testable.
//
// Toutes les colonnes sont créées SANS espace ni accent (nom interne = nom
// d'affichage, cf. CLAUDE.md « Colonne créée introuvable »). Aucune colonne
// Choix : rien à tenir à jour dans SharePoint, les valeurs viennent de l'appli.
// Aucune colonne Personne : les listes doivent pouvoir passer d'OP_SCE à QM_SCE.
// Liste de création pour Guillaume : docs/chantier-fnct.md § 6.

import type { ActionFnct, CategorieAction, EvenementFnct, Fnct, Jour } from '@/types/fnct.types'
import { CATEGORIES_ACTION } from '@/types/fnct.types'
import { jourParis } from './fnct'

export type TypeColonne = 'texte' | 'multiligne' | 'date' | 'dateheure' | 'nombre' | 'liste'

export interface DefColonne<T> {
  cle: keyof T & string
  /** Nom interne SharePoint. */
  colonne: string
  type: TypeColonne
  /** Libellé français (aperçu de l'import, guide de création). */
  libelle: string
  /** Colonne que l'import de l'Excel ne connaît pas : vide côté import, il ne l'efface jamais. */
  jamaisEffaceeParImport?: boolean
}

export const NOM_LISTE_FNCT = 'SCE_FNCT'
export const NOM_LISTE_ACTIONS = 'SCE_FNCT_Actions'
export const NOM_LISTE_HISTORIQUE = 'SCE_FNCT_Historique'

/** Title = N° de la FNC-T (`numero`). */
export const COLONNES_FNCT: readonly DefColonne<Fnct>[] = [
  { cle: 'annee', colonne: 'Annee', type: 'nombre', libelle: 'Année' },
  { cle: 'rang', colonne: 'Rang', type: 'nombre', libelle: 'Rang dans l’année' },
  { cle: 'enregistrement', colonne: 'DateEnregistrement', type: 'date', libelle: 'Enregistrée le' },
  { cle: 'enregistrePar', colonne: 'EnregistrePar', type: 'texte', libelle: 'Enregistrée par' },
  { cle: 'leader', colonne: 'Leader', type: 'texte', libelle: 'Leader' },
  { cle: 'expert', colonne: 'Expert', type: 'texte', libelle: 'Expert' },
  { cle: 'modele', colonne: 'Modele', type: 'texte', libelle: 'Modèle' },
  { cle: 'typeProduit', colonne: 'TypeProduit', type: 'texte', libelle: 'Type de produit' },
  { cle: 'numerosSerie', colonne: 'NumerosSerie', type: 'liste', libelle: 'N° de série' },
  { cle: 'cve', colonne: 'CVE', type: 'texte', libelle: 'CVE' },
  { cle: 'categorie', colonne: 'Categorie', type: 'texte', libelle: 'Catégorie' },
  { cle: 'documentsImpactes', colonne: 'DocumentsImpactes', type: 'multiligne', libelle: 'Documents / tests impactés' },
  { cle: 'description', colonne: 'DescriptionNC', type: 'multiligne', libelle: 'Description' },
  { cle: 'lieuDetection', colonne: 'LieuDetection', type: 'texte', libelle: 'Lieu de détection' },
  { cle: 'dateDetection', colonne: 'DateDetection', type: 'date', libelle: 'Détectée le' },
  { cle: 'priorite', colonne: 'Priorite', type: 'texte', libelle: 'Priorité' },
  { cle: 'explicationPriorite', colonne: 'ExplicationPriorite', type: 'multiligne', libelle: 'Justification de la priorité' },
  { cle: 'severite', colonne: 'Severite', type: 'nombre', libelle: 'Sévérité' },
  { cle: 'frequence', colonne: 'Frequence', type: 'nombre', libelle: 'Fréquence' },
  { cle: 'detectabilite', colonne: 'Detectabilite', type: 'nombre', libelle: 'Détectabilité' },
  { cle: 'analyseRisque', colonne: 'AnalyseRisque', type: 'texte', libelle: 'Analyse de risque' },
  { cle: 'clotureCorrection', colonne: 'DateClotureCorrection', type: 'date', libelle: 'Correction close le' },
  { cle: 'pretInvestigation', colonne: 'DatePretInvestigation', type: 'date', libelle: 'Prête pour investigation le' },
  { cle: 'clotureInvestigation', colonne: 'DateClotureInvestigation', type: 'date', libelle: 'Investigation close le' },
  { cle: 'numeroCapa', colonne: 'NumeroCAPA', type: 'texte', libelle: 'N° de CAPA' },
  { cle: 'commentaires', colonne: 'Commentaires', type: 'multiligne', libelle: 'Commentaires' },
  { cle: 'legitimite', colonne: 'Legitimite', type: 'texte', libelle: 'Légitimité' },
  { cle: 'cloture', colonne: 'DateCloture', type: 'date', libelle: 'Close le' },
  // Ajoutée le 07/10 : adresse du leader, en TEXTE (comme son nom) pour que les flows
  // la lisent et que la liste reste portable. Vide = personne hors annuaire.
  { cle: 'leaderUPN', colonne: 'LeaderUPN', type: 'texte', libelle: 'Adresse du leader', jamaisEffaceeParImport: true },
  // Ajoutées le 14/10 (relevé d'écarts P&ID / PPL, § 13). Les écarts n'existent pas dans
  // l'Excel ; l'ouverture y vaut la date d'enregistrement.
  { cle: 'ecarts', colonne: 'Ecarts', type: 'multiligne', libelle: 'Écarts relevés', jamaisEffaceeParImport: true },
  { cle: 'ouverture', colonne: 'Ouverture', type: 'date', libelle: 'Ouverte le', jamaisEffaceeParImport: true },
]

/** Title = ID de l'action (`FNC-T_2026-58-A01`). */
export const COLONNES_ACTIONS: readonly DefColonne<ActionFnct>[] = [
  { cle: 'fnct', colonne: 'FNCT', type: 'texte', libelle: 'FNC-T' },
  { cle: 'categorie', colonne: 'Categorie', type: 'texte', libelle: 'Catégorie' },
  { cle: 'description', colonne: 'DescriptionAction', type: 'multiligne', libelle: 'Description' },
  { cle: 'responsable', colonne: 'Responsable', type: 'texte', libelle: 'Responsable' },
  { cle: 'service', colonne: 'Service', type: 'texte', libelle: 'Service' },
  { cle: 'site', colonne: 'SiteResponsable', type: 'texte', libelle: 'Site' },
  { cle: 'ouverture', colonne: 'DateOuverture', type: 'date', libelle: 'Ouverte le' },
  { cle: 'echeance', colonne: 'Echeance', type: 'date', libelle: 'Échéance' },
  { cle: 'echeanceReportee', colonne: 'EcheanceReportee', type: 'date', libelle: 'Échéance reportée' },
  { cle: 'cloture', colonne: 'DateCloture', type: 'date', libelle: 'Close le' },
  { cle: 'commentaire', colonne: 'Commentaire', type: 'multiligne', libelle: 'Commentaire' },
  // Ajoutée le 07/10 : adresse du responsable (texte, voir LeaderUPN).
  { cle: 'responsableUPN', colonne: 'ResponsableUPN', type: 'texte', libelle: 'Adresse du responsable', jamaisEffaceeParImport: true },
]

/** Title = N° de la FNC-T (plusieurs lignes par FNC-T). */
export const COLONNES_HISTORIQUE: readonly DefColonne<EvenementFnct>[] = [
  { cle: 'date', colonne: 'DateEvenement', type: 'dateheure', libelle: 'Le' },
  { cle: 'auteur', colonne: 'Auteur', type: 'texte', libelle: 'Par' },
  { cle: 'type', colonne: 'TypeEvenement', type: 'texte', libelle: 'Événement' },
  { cle: 'contenu', colonne: 'Contenu', type: 'multiligne', libelle: 'Détail' },
]

/** Valeur d'un champ, telle qu'on la compare : texte, jour, nombre ou liste jointe. */
type Valeur = string | number | null

/** Forme comparable d'une valeur côté appli. */
function versComparable(v: unknown, type: TypeColonne): string {
  if (v === null || v === undefined) return ''
  if (type === 'liste') return Array.isArray(v) ? v.join(';') : String(v)
  return String(v).trim()
}

/** `['4023-H', '4024-H']` ↔ `;4023-H;4024-H;` (même format que ProjetsSuivis : `substringof(';X;', …)`). */
export function versListe(valeurs: readonly string[]): string {
  return valeurs.length ? `;${valeurs.join(';')};` : ''
}

export function depuisListe(v: unknown): string[] {
  return typeof v === 'string' ? v.split(';').map((x) => x.trim()).filter(Boolean) : []
}

/**
 * Une date écrite à MIDI UTC : quelle que soit l'heure d'été, elle reste le
 * même jour à Pompey. Relue en heure de Paris (une date saisie dans
 * SharePoint arrive en « 2026-01-26T23:00:00Z » pour le 27).
 */
export function versDateSharePoint(j: Jour): string {
  return `${j}T12:00:00Z`
}

export function depuisDateSharePoint(v: unknown): Jour | null {
  if (typeof v !== 'string' || !v) return null
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? null : jourParis(d)
}

/** Champ SharePoint à écrire ; null efface. */
function versChamp(v: unknown, type: TypeColonne): Valeur {
  if (v === null || v === undefined || v === '') return null
  switch (type) {
    case 'date':
      return versDateSharePoint(String(v))
    case 'dateheure':
      return String(v)
    case 'nombre':
      return typeof v === 'number' ? v : null
    case 'liste':
      return Array.isArray(v) ? versListe(v as string[]) || null : null
    case 'texte':
    case 'multiligne':
      return String(v)
    default: {
      const jamais: never = type
      return jamais
    }
  }
}

/**
 * Une colonne « Plusieurs lignes de texte » en TEXTE ENRICHI rend du HTML
 * (« <div class="ExternalClass6D04…"><div>…</div></div> » — 26/10 : la justification
 * de la priorité s'affichait telle quelle) : on n'en garde que le texte, une ligne
 * par paragraphe, entités décodées. Un texte brut passe sans changement.
 */
export function texteDepuisHtml(v: string): string {
  if (!/^\s*<(div|p|span|br)\b/i.test(v)) return v
  return v
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#x([0-9a-f]+);/gi, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, n: string) => String.fromCodePoint(Number(n)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/​/g, '')
    .replace(/ /g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim()
}

function depuisChamp(v: unknown, type: TypeColonne): unknown {
  switch (type) {
    case 'date':
      return depuisDateSharePoint(v)
    case 'dateheure':
      return typeof v === 'string' ? v : ''
    case 'nombre':
      return typeof v === 'number' ? v : v === null || v === undefined || v === '' ? null : Number(v)
    case 'liste':
      return depuisListe(v)
    case 'texte':
      return typeof v === 'string' ? v : ''
    case 'multiligne':
      return typeof v === 'string' ? texteDepuisHtml(v) : ''
    default: {
      const jamais: never = type
      return jamais
    }
  }
}

/** Les champs SharePoint d'un objet (Title compris). Les vides sont omis, sauf `avecVides`. */
export function versChamps<T>(
  objet: T,
  titre: string,
  colonnes: readonly DefColonne<T>[],
  avecVides = false,
): Record<string, Valeur> {
  const champs: Record<string, Valeur> = { Title: titre }
  for (const c of colonnes) {
    const v = versChamp(objet[c.cle], c.type)
    if (v !== null || avecVides) champs[c.colonne] = v
  }
  return champs
}

export function fnctDepuisChamps(itemId: string, f: Record<string, unknown>): Fnct {
  const o: Record<string, unknown> = { numero: String(f.Title ?? ''), itemId }
  for (const c of COLONNES_FNCT) o[c.cle] = depuisChamp(f[c.colonne], c.type)
  return o as unknown as Fnct
}

export function actionDepuisChamps(itemId: string, f: Record<string, unknown>): ActionFnct {
  const o: Record<string, unknown> = { id: String(f.Title ?? ''), itemId }
  for (const c of COLONNES_ACTIONS) o[c.cle] = depuisChamp(f[c.colonne], c.type)
  const cat = String(o.categorie ?? '')
  o.categorie = (CATEGORIES_ACTION as readonly string[]).includes(cat) ? (cat as CategorieAction) : ''
  return o as unknown as ActionFnct
}

export function evenementDepuisChamps(itemId: string, f: Record<string, unknown>): EvenementFnct {
  const o: Record<string, unknown> = { fnct: String(f.Title ?? ''), itemId }
  for (const c of COLONNES_HISTORIQUE) o[c.cle] = depuisChamp(f[c.colonne], c.type)
  return o as unknown as EvenementFnct
}

export interface Ecart {
  colonne: string
  libelle: string
  avant: string
  apres: string
}

/** Champs qui diffèrent entre ce que la base veut et ce que SharePoint porte. */
export function ecarts<T>(voulu: T, existant: T, colonnes: readonly DefColonne<T>[]): Ecart[] {
  const res: Ecart[] = []
  for (const c of colonnes) {
    const apres = versComparable(voulu[c.cle], c.type)
    const avant = versComparable(existant[c.cle], c.type)
    if (c.jamaisEffaceeParImport && !apres) continue
    if (apres !== avant) res.push({ colonne: c.colonne, libelle: c.libelle, avant, apres })
  }
  return res
}

/** Colonnes attendues absentes d'une liste (noms de l'appli retrouvés, cf. retrouverColonnes). */
export function colonnesManquantes<T>(colonnes: readonly DefColonne<T>[], presentes: readonly string[]): string[] {
  const set = new Set(presentes)
  return colonnes.map((c) => c.colonne).filter((c) => !set.has(c))
}

// ─── Noms internes : listes créées « à partir d'Excel » (07/10) ───
//
// Une liste créée depuis un fichier Excel garde le nom d'AFFICHAGE des colonnes
// (Annee, Leader…) mais leur donne des noms INTERNES `field_1`, `field_2`… — le
// cas de SCE_Problemes. Graph n'écrit et ne lit que par le nom interne : les
// colonnes sont donc retrouvées par leur nom (interne d'abord, d'affichage
// ensuite), une fois par session, et chaque lecture / écriture est traduite.
// Ça marche aussi pour une liste créée à la main, et après le déménagement sur
// QM_SCE, quels que soient les noms internes qu'elle reçoit.

/** Une colonne telle que Graph la décrit (`/lists/{id}/columns`). */
export interface ColonneLue {
  name: string
  displayName?: string
  readOnly?: boolean
  hidden?: boolean
  /** `textType` : `plain` ou `richText` (texte enrichi : du HTML à la lecture). */
  text?: { allowMultipleLines?: boolean; textType?: string }
  number?: object
  dateTime?: { format?: string }
}

/** Nom de colonne de l'appli → colonne SharePoint retrouvée. */
export type NomsInternes = ReadonlyMap<string, ColonneLue>

const nomComparable = (s: string | undefined): string => (s ?? '').trim().toLowerCase()

/** Préférence entre deux colonnes de même nom d'affichage : celle créée depuis Excel, puis une visible. */
const rangCandidat = (l: ColonneLue): number => (/^field_\d+$/.test(l.name) ? 0 : l.hidden ? 2 : 1)

export function retrouverColonnes<T>(colonnes: readonly DefColonne<T>[], lues: readonly ColonneLue[]): Map<string, ColonneLue> {
  const res = new Map<string, ColonneLue>()
  const modifiables = lues.filter((l) => !l.readOnly)
  for (const c of colonnes) {
    const l =
      modifiables.find((x) => x.name === c.colonne) ??
      modifiables
        .filter((x) => nomComparable(x.displayName) === nomComparable(c.colonne))
        .sort((a, b) => rangCandidat(a) - rangCandidat(b))[0]
    if (l) res.set(c.colonne, l)
  }
  return res
}

/** Champs à écrire : noms de l'appli → noms internes (Title et inconnus inchangés). */
export function versNomsInternes<V>(champs: Record<string, V>, noms: NomsInternes): Record<string, V> {
  return Object.fromEntries(Object.entries(champs).map(([k, v]) => [noms.get(k)?.name ?? k, v]))
}

/** Champs lus : chaque colonne de l'appli reprise sous son nom (les champs d'origine restent). */
export function depuisNomsInternes(fields: Record<string, unknown>, noms: NomsInternes): Record<string, unknown> {
  const res: Record<string, unknown> = { ...fields }
  for (const [nom, l] of noms) res[nom] = fields[l.name]
  return res
}

type TypeTrouve = 'texte' | 'multiligne' | 'nombre' | 'date' | 'dateheure' | 'autre'

function typeTrouve(l: ColonneLue): TypeTrouve {
  if (l.text) return l.text.allowMultipleLines ? 'multiligne' : 'texte'
  if (l.number) return 'nombre'
  if (l.dateTime) return l.dateTime.format === 'dateOnly' ? 'date' : 'dateheure'
  return 'autre'
}

/** Ce qui s'écrit sans perte dans chaque type attendu (une ligne de texte s'arrête à 255 caractères). */
const COMPATIBLES: Record<TypeColonne, readonly TypeTrouve[]> = {
  texte: ['texte', 'multiligne'],
  liste: ['texte', 'multiligne'],
  multiligne: ['multiligne'],
  nombre: ['nombre'],
  date: ['date', 'dateheure'],
  dateheure: ['dateheure'],
}

/** Libellés du choix de type dans SharePoint, comme l'onglet « À lire » des fichiers de création. */
const LIBELLES_TYPE: Record<TypeColonne | TypeTrouve, string> = {
  texte: 'Une ligne de texte',
  liste: 'Une ligne de texte',
  multiligne: 'Plusieurs lignes de texte',
  nombre: 'Nombre',
  date: 'Date et heure — date seule',
  dateheure: 'Date et heure — avec l’heure',
  autre: 'autre type',
}

export interface TypeACorriger {
  colonne: string
  attendu: string
  trouve: string
}

/** Colonnes retrouvées dont le type ferait rejeter ou abîmer l'écriture. */
export function typesACorriger<T>(colonnes: readonly DefColonne<T>[], noms: NomsInternes): TypeACorriger[] {
  const res: TypeACorriger[] = []
  for (const c of colonnes) {
    const l = noms.get(c.colonne)
    if (!l) continue
    const t = typeTrouve(l)
    if (!COMPATIBLES[c.type].includes(t)) res.push({ colonne: c.colonne, attendu: LIBELLES_TYPE[c.type], trouve: LIBELLES_TYPE[t] })
    // Texte enrichi (26/10) : SharePoint range le texte dans du HTML — à repasser en texte brut.
    else if (t === 'multiligne' && l.text?.textType === 'richText')
      res.push({ colonne: c.colonne, attendu: `${LIBELLES_TYPE.multiligne} — texte brut`, trouve: `${LIBELLES_TYPE.multiligne} — texte enrichi` })
  }
  return res
}
